import type { App, Component } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';
import NwComponents from '@skynet/nocware-components-vue3';
import { getGlobalStore } from '@skynet/frontend-shell';
import { createAppI18n } from './i18n';

// Pre-compiled NwComponents bundle — injected into document.head so the teleported
// NwDialog content (rendered inside .v-application) is styled correctly.
import nwComponentsCss from '@skynet/nocware-components-vue3/dist/nocware-components-vue3.css?inline';

// Defines the --nw-* theme custom properties (and fonts) that nwComponentsCss and
// our own SCSS read via theme-variants(). Without this, every themed color
// resolves to nothing. Must stay in document.head: it sets values on `:root`,
// which only matches the real document root — a copy inside the shadow root
// would be inert — but custom properties still inherit from there into the
// shadow tree, so this one file covers both.
import nwRootStylesCss from '@skynet/nocware-components-vue3/dist/wc/nw-root-styles.css?inline';

// Dialog-level overrides target .nw-dialog-inner which lives in the teleported DOM
// (inside .v-application), outside the component tree — must stay globally injected.
import chatDialogCss from './styles/chat-dialog.scss?inline';

// All widget component styles — must be in nwStyles so they are injected into the
// shadow root. Child .vue component <style> blocks are not collected automatically
// by Vue's defineCustomElement when customElement only matches .ce.vue files.
import chatComponentsCss from './styles/chat-components.scss?inline';

export const nwStyles = [nwRootStylesCss, nwComponentsCss, chatDialogCss, chatComponentsCss].join(
    '\n',
);

export function withNwStyles<T extends Component>(component: T): T {
    const base = component as T & { styles?: string[] };
    return { ...base, styles: [...(base.styles ?? []), nwStyles] } as T;
}

export function configureWcApp(app: App): void {
    app.use(createRouter({ history: createWebHashHistory(), routes: [] }));
    app.use(NwComponents);

    const i18n = createAppI18n();
    app.use(i18n);

    if (!document.getElementById('nw-chat-widget-styles')) {
        const styleEl = document.createElement('style');
        styleEl.id = 'nw-chat-widget-styles';
        styleEl.textContent = nwStyles;
        document.head.appendChild(styleEl);
    }

    const store = getGlobalStore();
    type Locale = typeof i18n.global.locale.value;
    i18n.global.locale.value = ((store.getStateByKey('lang') as string) ?? 'de') as Locale;
    store.subscribeToKey('lang', (lang) => {
        i18n.global.locale.value = ((lang as string) ?? 'de') as Locale;
    });
}
